/**
 * The window's drawings (ADR-0023): one `RootDrawings` per symbol **root** ("NQ" for NQZ6, NQH7
 * and NQ), shared by every chart of that root in this window — split panes, other chart modules
 * and pop-outs (same JS context) — so a drawing appears on all of them at once ("sync to all
 * charts of this symbol", TradingView's default for multi-chart layouts).
 *
 * Persistence: cached in local storage for an instant start and synced per user as the versioned
 * document `drawings/{root}` (SPEC §1A.3; whole-document type on the server — on a 409 the client
 * merges per drawing, `mergeDrawings`, and retries once). Saves are debounced. Undo / redo is per
 * root and per window (the `DrawingSet` history). The dev preview keeps drawings in local storage
 * only (`localOnlyDrawings`).
 */
import { useSyncExternalStore } from "react";
import { DocSync, useSettings } from "@helm/core";
import {
  DrawingSet,
  drawingDocBody,
  mergeDrawings,
  sanitizeDrawingDoc,
  sanitizeDrawingPrefs,
  type Drawing,
  type DrawingPrefs,
  type DrawingTx,
} from "@helm/core/drawings";
import { settingsSync } from "../../session";

const KEY = "helm:drawings:";
const PREVIEW_KEY = "helm:drawings:preview:";
const SAVE_DELAY_MS = 800;

let preview = false;

/** Dev preview: drawings live in local storage only, never the API. Call before first render. */
export function localOnlyDrawings(): void {
  preview = true;
}

function lsGet(k: string): string | null {
  try {
    return localStorage.getItem(k);
  } catch {
    return null;
  }
}

function lsSet(k: string, v: string): void {
  try {
    localStorage.setItem(k, v);
  } catch {
    // Quota / private mode: the server copy still holds them.
  }
}

type Meta = { version: number; dirty: boolean };

export class RootDrawings {
  readonly root: string;
  readonly set: DrawingSet;
  private readonly listeners = new Set<() => void>();
  private readonly docs = new DocSync();
  private meta: Meta = { version: 0, dirty: false };
  /** Server body at `meta.version` (the merge base). */
  private base: Drawing[] = [];
  private timer: ReturnType<typeof setTimeout> | null = null;
  private saving: Promise<void> | null = null;
  private started = false;

  constructor(root: string) {
    this.root = root;
    const key = this.key();
    let list: Drawing[] = [];
    try {
      list = sanitizeDrawingDoc(JSON.parse(lsGet(key) ?? "null"));
      const m = JSON.parse(lsGet(`${key}:sync`) ?? "null") as Partial<Meta> | null;
      if (m) this.meta = { version: typeof m.version === "number" ? m.version : 0, dirty: m.dirty === true };
    } catch {
      list = [];
    }
    this.set = new DrawingSet(list);
    this.base = list;
  }

  private key(): string {
    return `${preview ? PREVIEW_KEY : KEY}${this.root}`;
  }

  subscribe = (l: () => void): (() => void) => {
    this.listeners.add(l);
    void this.start();
    return () => {
      this.listeners.delete(l);
    };
  };

  getSnapshot = (): number => this.set.rev;

  get list(): readonly Drawing[] {
    return this.set.list();
  }

  private emit(): void {
    for (const l of this.listeners) l();
  }

  // ---- edits ------------------------------------------------------------------------------

  apply(tx: DrawingTx): boolean {
    if (!this.set.apply(tx)) return false;
    this.changed();
    return true;
  }

  undo(): boolean {
    if (!this.set.undo()) return false;
    this.changed();
    return true;
  }

  redo(): boolean {
    if (!this.set.redo()) return false;
    this.changed();
    return true;
  }

  private changed(): void {
    this.meta = { ...this.meta, dirty: true };
    this.persistLocal();
    this.emit();
    this.scheduleSave();
  }

  private persistLocal(): void {
    lsSet(this.key(), JSON.stringify(drawingDocBody(this.set.list())));
    lsSet(`${this.key()}:sync`, JSON.stringify(this.meta));
  }

  // ---- server ------------------------------------------------------------------------------

  private async start(): Promise<void> {
    if (preview || this.started) return;
    this.started = true;
    try {
      const doc = await this.docs.load<Record<string, unknown>>("drawings", this.root);
      if (doc) {
        const server = sanitizeDrawingDoc(doc.body);
        if (!this.meta.dirty || doc.version > this.meta.version) {
          // A newer server copy: keep local edits made on top of the old base.
          const merged = this.meta.dirty ? mergeDrawings(this.base, this.set.list(), server) : server;
          this.adopt(server, doc.version, merged);
          if (this.meta.dirty) await this.save();
          return;
        }
        this.base = server;
        this.meta = { ...this.meta, version: doc.version };
      }
      if (this.meta.dirty) await this.save();
    } catch {
      // Offline / no API: local copy only; the next edit retries.
    }
  }

  /** `DocChanged` for drawings/{root}: another device or window saved. */
  async refresh(version: number): Promise<void> {
    if (preview || version <= this.meta.version) return;
    try {
      const doc = await this.docs.load<Record<string, unknown>>("drawings", this.root);
      if (!doc || doc.version <= this.meta.version) return;
      const server = sanitizeDrawingDoc(doc.body);
      const merged = this.meta.dirty ? mergeDrawings(this.base, this.set.list(), server) : server;
      this.adopt(server, doc.version, merged);
    } catch {
      /* next edit or reload catches up */
    }
  }

  private adopt(server: Drawing[], version: number, list: readonly Drawing[]): void {
    this.base = server;
    const dirty = JSON.stringify(list) !== JSON.stringify(server);
    this.meta = { version, dirty };
    if (JSON.stringify(list) !== JSON.stringify(this.set.list())) {
      this.set.replaceAll(list);
      this.emit();
    }
    this.persistLocal();
  }

  private scheduleSave(): void {
    if (preview) return;
    if (this.timer !== null) clearTimeout(this.timer);
    this.timer = setTimeout(() => {
      this.timer = null;
      void this.save();
    }, SAVE_DELAY_MS);
  }

  async save(): Promise<void> {
    if (preview) return;
    if (this.saving) {
      await this.saving;
      if (this.meta.dirty) await this.save();
      return;
    }
    this.saving = this.saveOnce();
    try {
      await this.saving;
    } finally {
      this.saving = null;
    }
  }

  private async saveOnce(retry = true): Promise<void> {
    const local = [...this.set.list()];
    const rev = this.set.rev;
    try {
      const r = await this.docs.save(
        "drawings",
        this.root,
        drawingDocBody(this.base),
        this.meta.version,
        drawingDocBody(local),
      );
      if (r.kind === "saved") {
        const server = sanitizeDrawingDoc(r.doc.body);
        this.base = server;
        this.meta = { version: r.doc.version, dirty: this.set.rev !== rev };
        this.persistLocal();
        if (this.meta.dirty) this.scheduleSave();
        return;
      }
      // 409 (whole-document type): merge per drawing and retry once on the server's version.
      const server = sanitizeDrawingDoc(r.server.body);
      const merged = mergeDrawings(this.base, this.set.list(), server);
      this.adopt(server, r.server.version, merged);
      if (retry && this.meta.dirty) await this.saveOnce(false);
    } catch {
      // Offline: stays dirty; retried on the next edit or reload.
    }
  }

  flush(): void {
    if (this.timer === null) return;
    clearTimeout(this.timer);
    this.timer = null;
    void this.save();
  }
}

const roots = new Map<string, RootDrawings>();

export function rootDrawings(root: string): RootDrawings {
  let r = roots.get(root);
  if (!r) {
    r = new RootDrawings(root);
    roots.set(root, r);
  }
  return r;
}

/** Route a drawings `DocChanged` (session.ts) to the root's store, if this window has it. */
export function drawingsDocChanged(docId: string, version: number): void {
  void roots.get(docId)?.refresh(version);
}

/** Flush pending saves (page hide). */
export function flushDrawings(): void {
  for (const r of roots.values()) r.flush();
}

if (typeof window !== "undefined") {
  window.addEventListener("pagehide", flushDrawings);
}

/** Re-render on any change of `root`'s drawings; returns the list (z order). */
export function useRootDrawings(root: string | null): readonly Drawing[] {
  const r = root ? rootDrawings(root) : null;
  useSyncExternalStore(r ? r.subscribe : noopSubscribe, r ? r.getSnapshot : zero);
  return r ? r.list : EMPTY;
}

const EMPTY: readonly Drawing[] = [];
const noopSubscribe = (): (() => void) => () => {};
const zero = (): number => 0;

// ---- preferences (settings/main → drawing_prefs) ---------------------------------------------

export function useDrawingPrefs(): DrawingPrefs {
  const raw = useSettings((s) => s.settings["drawing_prefs"]);
  return sanitizeDrawingPrefs(raw);
}

export function currentDrawingPrefs(): DrawingPrefs {
  return sanitizeDrawingPrefs(useSettings.getState().settings["drawing_prefs"]);
}

export function updateDrawingPrefs(patch: Partial<DrawingPrefs>): void {
  const next = { ...currentDrawingPrefs(), ...patch };
  void settingsSync.update({ drawing_prefs: next });
}

// ---- clipboard (Ctrl+C / Ctrl+V between charts of this window) --------------------------------

let clipboard: { root: string; drawings: Drawing[] } | null = null;

export function copyDrawings(root: string, list: Drawing[]): void {
  clipboard = { root, drawings: list.map((d) => ({ ...d })) };
}

export function clipboardDrawings(): { root: string; drawings: Drawing[] } | null {
  return clipboard;
}
